(ns ui.components.sortable-list.examples
  (:require
   [reagent.core :as r]
   [ui.components.sortable-list.view :as sortable-list]
   [ui.components.button.view :as button]
   [ui.util :as uu]))

;; Sample data
(def sample-items
  [{:id "1" :label "First Item" :priority "High"}
   {:id "2" :label "Second Item" :priority "Medium"}
   {:id "3" :label "Third Item" :priority "Low"}
   {:id "4" :label "Fourth Item" :priority "High"}])

;; Basic vertical list - full item draggable
(defn basic-vertical-example
  []
  (let [items* (r/atom sample-items)]
    (fn []
      [:div {:class "space-y-4"}
       [:h3 {:class "text-lg font-semibold"} "Basic Vertical List - Full Item Draggable"]
       [:p {:class "text-sm text-gray-600"} "Drag any part of the item to reorder"]

       [sortable-list/sortable-list
        {:items @items*
         :get-key :id
         :drag-handle? false
         :on-reorder (fn [{:keys [source-index destination-index]}]
                       (swap! items* #(sortable-list/reorder-items % source-index destination-index)))
         :render-item (fn [{:keys [item index dragging?]}]
                        (js/console.log "draggig" dragging?)
                        [:div
                         {:class (uu/cm "flex items-center gap-3 px-3 py-2 border rounded-lg cursor-grab hover:cursor-grabbing"
                                        (when dragging? "shadow-lg cursor-grabbing"))}
                         [:div {:class "flex-1"}
                          [:p {:class "font-medium"} (:label item)]
                          [:p {:class "text-sm text-gray-500"} (str "Priority: " (:priority item))]]
                         [:span {:class "text-xs text-gray-400"} (str "#" (inc index))]])}]])))

;; Horizontal list - full item draggable
(defn horizontal-example
  []
  (let [items* (r/atom (take 6 sample-items))]
    (fn []
      [:div {:class "space-y-4"}
       [:h3 {:class "text-lg font-semibold"} "Horizontal List - Full Item Draggable"]
       [:p {:class "text-sm text-gray-600"} "Horizontal orientation with full item dragging"]

       [:div
        {:class "p-2 bg-blue-500"}
        [sortable-list/sortable-list
         {:items @items*
          :get-key :id
          :orientation :horizontal
          :drag-handle? false
          :gap 10
          :container-class "p-2 rounded-lg border"
          :on-reorder (fn [{:keys [source-index destination-index]}]
                        (swap! items* #(sortable-list/reorder-items % source-index destination-index)))
          :render-item (fn [{:keys [item dragging?]}]
                         [:div
                          {:class (uu/cm "min-w-[120px] px-3 py-2 bg-white border rounded-lg cursor-grab hover:cursor-grabbing"
                                         (when dragging? "shadow-lg cursor-grabbing"))}
                          [:p {:class "font-medium text-sm"} (:label item)]
                          [:p {:class "text-xs text-gray-500"} (:priority item)]])}]]])))

;; Card list example - full item draggable
(defn card-list-example
  []
  (let [items* (r/atom sample-items)]
    (fn []
      [:div {:class "space-y-4"}
       [:h3 {:class "text-lg font-semibold"} "Card List - Full Item Draggable"]
       [:p {:class "text-sm text-gray-600"} "Card-style layout with full item dragging"]

       [sortable-list/sortable-list
        {:items @items*
         :get-key :id
         :drag-handle? false
         :gap        10
         :on-reorder (fn [{:keys [source-index destination-index]}]
                       (swap! items* #(sortable-list/reorder-items % source-index destination-index)))
         :render-item (fn [{:keys [item dragging?]}]
                        [:div
                         {:class (uu/cm "p-4 bg-white border rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-grab hover:cursor-grabbing"
                                        (when dragging? "shadow-lg cursor-grabbing"))}
                         [:div {:class "flex items-start justify-between"}
                          [:div
                           [:h4 {:class "font-semibold text-gray-900"} (:label item)]
                           [:p {:class "text-sm text-gray-600 mt-1"} (str "Priority: " (:priority item))]]
                          [:div {:class "flex items-center space-x-2"}
                           [:span {:class (uu/cm "px-2 py-1 rounded-full text-xs font-medium"
                                                 (case (:priority item)
                                                   "High" "bg-red-100 text-red-800"
                                                   "Medium" "bg-yellow-100 text-yellow-800"
                                                   "Low" "bg-green-100 text-green-800"
                                                   "bg-gray-100 text-gray-800"))}
                            (:priority item)]]]])}]])))

;; Drag handle example (one of the few with handles)
(defn drag-handle-example
  []
  (let [items* (r/atom sample-items)]
    (fn []
      [:div {:class "space-y-4"}
       [:h3 {:class "text-lg font-semibold"} "Drag Handle Example"]
       [:p {:class "text-sm text-gray-600"} "Use drag handle when item contains interactive elements"]

       [sortable-list/sortable-list
        {:items @items*
         :get-key :id
         :on-reorder (fn [{:keys [source-index destination-index]}]
                       (swap! items* #(sortable-list/reorder-items % source-index destination-index)))
         :render-item (fn [{:keys [item dragging? drag-handle]}]
                        [:div
                         {:class (uu/cm "flex items-center gap-3 px-3 py-2 bg-white border rounded-lg"
                                        (when dragging? "shadow-lg"))}
                         drag-handle
                         [:div {:class "flex-1"}
                          [:p {:class "font-medium"} (:label item)]
                          [:p {:class "text-sm text-gray-500"} (str "Priority: " (:priority item))]]
                         [:button {:class "px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                   :on-click #(js/alert (str "Clicked " (:label item)))}
                          "Action"]])}]])))

;; Todo list example - full item draggable
(defn todo-list-example
  []
  (let [todos* (r/atom [{:id "1" :text "Design API" :completed? false}
                        {:id "2" :text "Implement component" :completed? true}
                        {:id "3" :text "Write examples" :completed? false}
                        {:id "4" :text "Write tests" :completed? false}])]
    (fn []
      [:div {:class "space-y-4"}
       [:h3 {:class "text-lg font-semibold"} "Todo List - Full Item Draggable"]
       [:p {:class "text-sm text-gray-600"} "Interactive todo list with full item dragging"]

       [sortable-list/sortable-list
        {:items @todos*
         :get-key :id
         :drag-handle? false
         :on-reorder (fn [{:keys [source-index destination-index]}]
                       (swap! todos* #(sortable-list/reorder-items % source-index destination-index)))
         :render-item (fn [{:keys [item dragging?]}]
                        [:div
                         {:class (uu/cm "flex items-center gap-3 px-3 py-2 bg-white border rounded-lg cursor-grab hover:cursor-grabbing"
                                        (when dragging? "shadow-lg cursor-grabbing")
                                        (when (:completed? item) "bg-gray-50"))}
                         [:input
                          {:type "checkbox"
                           :checked (:completed? item)
                           :on-change #(swap! todos*
                                              (fn [todos]
                                                (mapv (fn [todo]
                                                        (if (= (:id todo) (:id item))
                                                          (update todo :completed? not)
                                                          todo))
                                                      todos)))
                           :class "rounded"}]
                         [:div {:class "flex-1"}
                          [:p {:class (uu/cm "font-medium"
                                             (when (:completed? item) "line-through text-gray-500"))}
                           (:text item)]]
                         [button/square-secondary-open
                          {:on-click #(swap! todos* (fn [todos]
                                                      (filterv (fn [todo]
                                                                 (not= (:id todo) (:id item)))
                                                               todos)))
                           :class "text-red-500 hover:text-red-700"}
                          [:i {:class "fas fa-trash text-xs"}]]])}]])))

;; Auto-scroll example with many items
(defn auto-scroll-example
  []
  (let [items* (r/atom (vec (for [i (range 1 101)]
                              {:id (str i)
                               :label (str "Item " i)
                               :category (rand-nth ["Work" "Personal" "Important" "Later"])})))]
    (fn []
      [:div {:class "space-y-4"}
       [:h3 {:class "text-lg font-semibold"} "Auto-scroll Example - 100 Items"]
       [:p {:class "text-sm text-gray-600"}
        "Constrained container with 100 items. Drag items near the top/bottom edges to trigger auto-scrolling."]

       [:div {:class "h-96 border rounded-lg overflow-hidden"}
        [sortable-list/sortable-list
         {:items @items*
          :get-key :id
          :drag-handle? false
          :gap 2
          :container-class "p-3"
          :auto-scroll? true
          :on-reorder (fn [{:keys [source-index destination-index]}]
                        (swap! items* #(sortable-list/reorder-items % source-index destination-index)))
          :render-item (fn [{:keys [item index dragging?]}]
                         [:div
                          {:class (uu/cm "flex items-center justify-between px-3 py-1.5 bg-white border rounded cursor-grab hover:cursor-grabbing hover:bg-gray-50"
                                         (when dragging? "shadow-lg cursor-grabbing"))}
                          [:div {:class "flex items-center gap-2"}
                           [:span {:class "text-xs text-gray-400 font-mono w-8"} (str "#" (inc index))]
                           [:span {:class "font-medium text-sm"} (:label item)]]
                          [:span {:class (uu/cm "px-2 py-0.5 rounded text-xs font-medium"
                                                (case (:category item)
                                                  "Work" "bg-blue-100 text-blue-800"
                                                  "Personal" "bg-green-100 text-green-800"
                                                  "Important" "bg-red-100 text-red-800"
                                                  "Later" "bg-gray-100 text-gray-800"
                                                  "bg-gray-100 text-gray-800"))}
                           (:category item)]])}]]])))

;; Different heights example
(defn different-heights-example
  []
  (let [items* (r/atom [{:id "1"
                         :title "Short Task"
                         :description "A simple task"
                         :type :compact}
                        {:id "2"
                         :title "Medium Task with Description"
                         :description "This task has a longer description that explains what needs to be done. It should take up more vertical space than the short task."
                         :tags ["urgent" "backend"]
                         :type :medium}
                        {:id "3"
                         :title "Minimal"
                         :type :compact}
                        {:id "4"
                         :title "Large Task with Lots of Content"
                         :description "This is a comprehensive task that requires detailed explanation. It contains multiple paragraphs of information and various metadata that makes it significantly taller than other items. This demonstrates how the sortable list handles items of varying heights during drag operations."
                         :tags ["frontend" "design" "review"]
                         :notes "Additional notes: Remember to check with the design team before implementing. This task might require coordination with multiple stakeholders."
                         :type :large}
                        {:id "5"
                         :title "Another Short One"
                         :description "Brief task"
                         :type :compact}
                        {:id "6"
                         :title "Complex Multi-Step Process"
                         :description "This task involves multiple steps and requires careful planning. Each step needs to be executed in the correct order."
                         :tags ["backend" "database" "migration"]
                         :steps ["Step 1: Analyze current data structure"
                                 "Step 2: Design migration strategy"
                                 "Step 3: Implement changes"
                                 "Step 4: Test thoroughly"]
                         :type :large}])]
    (fn []
      [:div {:class "space-y-4"}
       [:h3 {:class "text-lg font-semibold"} "Different Heights Example"]
       [:p {:class "text-sm text-gray-600"} "Items with varying heights - compact, medium, and large cards"]

       [sortable-list/sortable-list
        {:items @items*
         :get-key :id
         :drag-handle? false
         :gap 8
         :on-reorder (fn [{:keys [source-index destination-index]}]
                       (swap! items* #(sortable-list/reorder-items % source-index destination-index)))
         :render-item (fn [{:keys [item dragging?]}]
                        [:div
                         {:class (uu/cm "p-4 bg-white border rounded-lg cursor-grab hover:cursor-grabbing hover:shadow-sm transition-shadow"
                                        (when dragging? "shadow-lg cursor-grabbing"))}
                         [:div {:class "space-y-2"}
                          ;; Title
                          [:h4 {:class "font-semibold text-gray-900"} (:title item)]

                          ;; Description (if present)
                          (when (:description item)
                            [:p {:class "text-sm text-gray-600"} (:description item)])

                          ;; Tags (if present)
                          (when (:tags item)
                            [:div {:class "flex gap-1 flex-wrap"}
                             (for [tag (:tags item)]
                               ^{:key tag}
                               [:span {:class "px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"}
                                tag])])

                          ;; Steps (if present)
                          (when (:steps item)
                            [:div {:class "space-y-1"}
                             [:p {:class "text-xs font-medium text-gray-700"} "Steps:"]
                             [:ol {:class "text-xs text-gray-600 space-y-1 ml-4"}
                              (for [step (:steps item)]
                                ^{:key step}
                                [:li step])]])

                          ;; Notes (if present)
                          (when (:notes item)
                            [:div {:class "mt-2 p-2 bg-yellow-50 border-l-4 border-yellow-200"}
                             [:p {:class "text-xs text-yellow-800"} (:notes item)]])

                          ;; Type indicator
                          [:div {:class "flex justify-end"}
                           [:span {:class (uu/cm "px-2 py-1 text-xs rounded"
                                                 (case (:type item)
                                                   :compact "bg-green-100 text-green-800"
                                                   :medium "bg-yellow-100 text-yellow-800"
                                                   :large "bg-red-100 text-red-800"
                                                   "bg-gray-100 text-gray-800"))}
                            (name (:type item))]]]])}]])))

;; Main examples component
(defn examples
  []
  [:div {:class "max-w-4xl mx-auto p-6 space-y-12"}
   [:div
    [:h1 {:class "text-3xl font-bold mb-2"} "Sortable List Component Examples"]
    [:p {:class "text-gray-600"}
     "Demonstrations of the sortable list component with different configurations."]]

   #_[basic-vertical-example]

   [different-heights-example]
   [auto-scroll-example]
   [horizontal-example]
   [card-list-example]
   [drag-handle-example]
   [:div
    {:class "bg-red-200"}
    [todo-list-example]]]) 
